"use client";

import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTranslation } from "@/hooks/useTranslation";
import { useRTL } from "@/hooks/useRTL";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  ChevronUp,
  HelpCircle,
  MessageCircle,
  Phone,
  Mail,
  Clock,
  Shield,
  DollarSign,
  Calendar,
  Sparkles,
  AlertTriangle,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

export default function FAQSection() {
  const { t, isLoading } = useTranslation("teeth-whitening");
  const { isRTL } = useRTL();
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState("general");
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const faqRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoading) return;

    const ctx = gsap.context(() => {
      // Title animation
      if (titleRef.current) {
        gsap.fromTo(
          titleRef.current,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: titleRef.current,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }

      // FAQ animation
      if (faqRef.current) {
        gsap.fromTo(
          faqRef.current,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out",
            scrollTrigger: {
              trigger: faqRef.current,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, [isLoading]);

  const toggleItem = (id: string) => {
    setOpenItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const categories = [
    {
      id: "general",
      icon: HelpCircle,
      label: t("faq.categories.general"),
      color: "from-blue-500 to-blue-600",
    },
    {
      id: "procedure",
      icon: Sparkles,
      label: t("faq.categories.procedure"),
      color: "from-purple-500 to-purple-600",
    },
    {
      id: "aftercare",
      icon: Shield,
      label: t("faq.categories.aftercare"),
      color: "from-green-500 to-green-600",
    },
    {
      id: "cost",
      icon: DollarSign,
      label: t("faq.categories.cost"),
      color: "from-orange-500 to-orange-600",
    },
  ];

  const faqData = {
    general: [
      {
        id: "general-1",
        question: t("faq.general.what_is_whitening.question"),
        answer: t("faq.general.what_is_whitening.answer"),
      },
      {
        id: "general-2",
        question: t("faq.general.how_long_lasts.question"),
        answer: t("faq.general.how_long_lasts.answer"),
      },
      {
        id: "general-3",
        question: t("faq.general.safe_procedure.question"),
        answer: t("faq.general.safe_procedure.answer"),
      },
      {
        id: "general-4",
        question: t("faq.general.age_requirements.question"),
        answer: t("faq.general.age_requirements.answer"),
      },
    ],
    procedure: [
      {
        id: "procedure-1",
        question: t("faq.procedure.how_long_appointment.question"),
        answer: t("faq.procedure.how_long_appointment.answer"),
      },
      {
        id: "procedure-2",
        question: t("faq.procedure.painful_process.question"),
        answer: t("faq.procedure.painful_process.answer"),
      },
      {
        id: "procedure-3",
        question: t("faq.procedure.how_many_sessions.question"),
        answer: t("faq.procedure.how_many_sessions.answer"),
      },
      {
        id: "procedure-4",
        question: t("faq.procedure.immediate_results.question"),
        answer: t("faq.procedure.immediate_results.answer"),
      },
    ],
    aftercare: [
      {
        id: "aftercare-1",
        question: t("faq.aftercare.foods_to_avoid.question"),
        answer: t("faq.aftercare.foods_to_avoid.answer"),
      },
      {
        id: "aftercare-2",
        question: t("faq.aftercare.sensitivity_duration.question"),
        answer: t("faq.aftercare.sensitivity_duration.answer"),
      },
      {
        id: "aftercare-3",
        question: t("faq.aftercare.maintain_results.question"),
        answer: t("faq.aftercare.maintain_results.answer"),
      },
      {
        id: "aftercare-4",
        question: t("faq.aftercare.when_normal_diet.question"),
        answer: t("faq.aftercare.when_normal_diet.answer"),
      },
    ],
    cost: [
      {
        id: "cost-1",
        question: t("faq.cost.treatment_cost.question"),
        answer: t("faq.cost.treatment_cost.answer"),
      },
      {
        id: "cost-2",
        question: t("faq.cost.insurance_coverage.question"),
        answer: t("faq.cost.insurance_coverage.answer"),
      },
      {
        id: "cost-3",
        question: t("faq.cost.payment_options.question"),
        answer: t("faq.cost.payment_options.answer"),
      },
      {
        id: "cost-4",
        question: t("faq.cost.package_differences.question"),
        answer: t("faq.cost.package_differences.answer"),
      },
    ],
  };

  const currentFAQs = faqData[activeCategory as keyof typeof faqData] || [];

  if (isLoading) {
    return (
      <section className="py-16 lg:py-24 bg-slate-50 dark:bg-slate-900">
        <div className="container">
          <div className="text-center mb-16 animate-pulse">
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded mb-6 max-w-4xl mx-auto"></div>
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded max-w-3xl mx-auto"></div>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="h-16 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse"
                ></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="faq"
      ref={sectionRef}
      className="py-16 lg:py-24 bg-slate-50 dark:bg-slate-900"
    >
      <div className="container">
        <div ref={titleRef} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
            {t("faq.title.part1")}{" "}
            <span className="text-gold">{t("faq.title.part2")}</span>
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
            {t("faq.subtitle")}
          </p>
        </div>

        <div ref={faqRef} className="max-w-6xl mx-auto">
          {/* Category Navigation */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  activeCategory === category.id
                    ? "bg-gold text-navy shadow-lg"
                    : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-gold/50"
                }`}
              >
                <category.icon className="w-5 h-5" />
                {category.label}
              </button>
            ))}
          </div>

          {/* FAQ Items */}
          <div className="space-y-4 mb-16">
            {currentFAQs.map((faq, index) => (
              <Card
                key={faq.id}
                className="border-slate-200 dark:border-slate-700 hover:shadow-lg transition-all duration-300 hover:border-gold/50 bg-white dark:bg-slate-800"
              >
                <CardContent className="p-0">
                  <button
                    onClick={() => toggleItem(faq.id)}
                    className={`w-full p-6 text-left flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors duration-200 ${
                      isRTL ? "text-right" : "text-left"
                    }`}
                  >
                    <h3 className="text-lg font-semibold text-navy dark:text-white flex-1 pr-4">
                      {faq.question}
                    </h3>
                    {openItems.includes(faq.id) ? (
                      <ChevronUp className="w-5 h-5 text-gold flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-600 dark:text-slate-300 flex-shrink-0" />
                    )}
                  </button>

                  {openItems.includes(faq.id) && (
                    <div className="px-6 pb-6">
                      <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                        <p
                          className={`text-slate-600 dark:text-slate-300 leading-relaxed ${
                            isRTL ? "text-right" : "text-left"
                          }`}
                        >
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
